import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useState } from 'react';
import { useMutation } from 'data-fetching-client';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { CharacterCounter } from 'foundations-components/transitional/components/CharacterCounter';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { Flex } from 'foundations-components/components/Flex';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Text } from 'foundations-components/components/Text';
import { TextArea } from 'foundations-components/transitional/components/TextArea';
import { View } from 'foundations-components/components/View';
import { logError } from '../../../app/utils/handleError';
import { TrackingScreen, trackGiveFeedbackButtonSubmit } from '../../../app/utils/tracking';
import { GET_ROLLOUT_PRODUCT_UPDATE_BY_ROLLOUT_ID, GET_ROLLOUT_PRODUCT_UPDATE_BY_UPDATE_ID } from '../../queries';
import useUpdatePUTableCache from '../../hooks/useUpdatePUTableCache';
import { FeedbackBtnClickTrackerContext } from '../../contexts/Tracking';
import { SUBMIT_FEEDBACK_MUTATION } from './queries';
import CsatNegative from './CsatNegative';
import CsatNeutral from './CsatNeutral';
import CsatPositive from './CsatPositive';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const MAX_FEEDBACK_LENGTH = 1500;
export const STEPS = ['rating', 'feedback'];
/**
 * This component should be used inside a form.
 */
const BetaFeedbackFormSection = /*#__PURE__*/forwardRef(({
  rolloutId,
  productUpdateId,
  onStepChangeCb,
  onCompletedCb,
  onErrorCb
}, ref) => {
  const {
    subscreen,
    origin,
    rolloutState,
    untranslatedPUTitle
  } = useContext(FeedbackBtnClickTrackerContext);
  const [step, setStep] = useState('rating');
  const [rating, setRating] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [handRaised, setHandRaised] = useState(true);
  const {
    updateCache
  } = useUpdatePUTableCache();
  const [submitFeedback] = useMutation(SUBMIT_FEEDBACK_MUTATION, {
    refetchQueries: [{
      query: GET_ROLLOUT_PRODUCT_UPDATE_BY_ROLLOUT_ID,
      variables: {
        id: rolloutId
      }
    }, {
      query: GET_ROLLOUT_PRODUCT_UPDATE_BY_UPDATE_ID,
      variables: {
        id: productUpdateId
      }
    }],
    onCompleted: data => {
      var _data$submitFeedback;
      if (data.submitFeedback.type === 'rpcError') {
        logError({
          componentName: 'BetaFeedbackFormSection',
          error: data.submitFeedback.rpcError
        });
        onErrorCb === null || onErrorCb === void 0 || onErrorCb(data.submitFeedback.rpcError);
        return;
      }
      updateCache(productUpdateId, {
        userFeedbackSubmissionInfo: {
          latestSubmissionTimeStamp: Date.now()
        }
      });
      onCompletedCb === null || onCompletedCb === void 0 || onCompletedCb(data === null || data === void 0 || (_data$submitFeedback = data.submitFeedback) === null || _data$submitFeedback === void 0 || (_data$submitFeedback = _data$submitFeedback.data) === null || _data$submitFeedback === void 0 || (_data$submitFeedback = _data$submitFeedback.submissionRequest) === null || _data$submitFeedback === void 0 ? void 0 : _data$submitFeedback.feedbackRequest);
      return;
    },
    onError: error => {
      logError({
        componentName: 'BetaFeedbackFormSection',
        error
      });
      onErrorCb === null || onErrorCb === void 0 || onErrorCb(error);
    }
  });
  useEffect(() => {
    if (onStepChangeCb) {
      onStepChangeCb(step);
    }
  }, [step, onStepChangeCb]);
  const onRateSelected = selectedRating => {
    setRating(selectedRating);
    setStep('feedback');
  };
  const onSubmit = useCallback(() => {
    if (rating === null) {
      return;
    }
    trackGiveFeedbackButtonSubmit({
      screen: TrackingScreen.PRODUCT_UPDATES,
      subscreen,
      origin,
      rolloutState,
      productUpdateId: `${productUpdateId}`,
      productUpdateTitle: untranslatedPUTitle,
      rating,
      withFeedback: Boolean(feedback && feedback.trim()),
      handRaised
    });
    void submitFeedback({
      variables: {
        rating,
        feedback: feedback || undefined,
        handRaised,
        rolloutId,
        productUpdateId
      }
    });
  }, [rating, subscreen, origin, rolloutState, feedback, handRaised, rolloutId, productUpdateId, untranslatedPUTitle, submitFeedback]);
  useImperativeHandle(ref, () => ({
    onSubmit,
    getFeedback: () => ({
      rating,
      feedback,
      handRaised
    })
  }), [feedback, handRaised, onSubmit, rating]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [step === 'rating' && /*#__PURE__*/_jsx(View, {
      marginBlockStart: "300",
      children: /*#__PURE__*/_jsxs(Flex, {
        justifyContent: "center",
        children: [/*#__PURE__*/_jsx(View, {
          "data-test-id": "csat-negative",
          onClick: () => onRateSelected('NEGATIVE'),
          children: /*#__PURE__*/_jsx(CsatNegative, {
            size: 64
          })
        }), /*#__PURE__*/_jsx(View, {
          "data-test-id": "csat-neutral",
          onClick: () => onRateSelected('NEUTRAL'),
          children: /*#__PURE__*/_jsx(CsatNeutral, {
            size: 64
          })
        }), /*#__PURE__*/_jsx(View, {
          "data-test-id": "csat-positive",
          onClick: () => onRateSelected('POSITIVE'),
          children: /*#__PURE__*/_jsx(CsatPositive, {
            size: 64
          })
        })]
      })
    }), step === 'feedback' && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(CharacterCounter, {
        marginBlockStart: "300",
        counter: "down",
        max: MAX_FEEDBACK_LENGTH,
        renderInput: ({
          count,
          onChange,
          value
        }) => /*#__PURE__*/_jsx(FormControl, {
          characterCount: count,
          children: /*#__PURE__*/_jsx(TextArea, {
            maxLength: MAX_FEEDBACK_LENGTH,
            value: value,
            error: count <= 0,
            onChange: e => {
              setFeedback(e.target.value);
              onChange(e);
            },
            rows: 3,
            placeholder: unescapedText('app.betaFeedback.modal.feedbackPlaceholder')
          })
        })
      }), /*#__PURE__*/_jsx(View, {
        marginBlockStart: "300",
        marginBlockEnd: "600",
        children: /*#__PURE__*/_jsx(Checkbox, {
          checked: handRaised,
          onChange: () => {
            setHandRaised(checked => !checked);
          },
          alignment: "start",
          children: /*#__PURE__*/_jsx(Text, {
            typography: "body-100",
            textAlign: "left",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app.betaFeedback.modal.handRaiseLabel"
            })
          })
        })
      })]
    })]
  });
});
export default BetaFeedbackFormSection;